/**
 * Config-count labels for spec tables.
 *
 * Same pattern already shipped on /search ("Configuration 1 of 2"): when a
 * table lists several rows for one year (different engine, drivetrain, bed or
 * body configuration), an unlabelled repeat of the same year reads like a
 * duplicate. Labelling each repeat tells the reader the rows are distinct
 * configurations, not a data error.
 *
 * Rows that are the only entry for their year get no label.
 */
export interface ConfigRow {
  year?: number | null;
  trim?: string | null;
  variant_label?: string | null;
}

/** Group key: one group per model year. */
function groupKey(row: ConfigRow): string {
  return String(row.year ?? "");
}

/**
 * Returns a label per row index — `"Config 2 of 3"` — or `null` when that year
 * has a single row.
 */
const cache = new WeakMap<object, (string | null)[]>();

export function configLabels(rows: ConfigRow[]): (string | null)[] {
  const hit = cache.get(rows);
  if (hit) return hit;
  const out = computeConfigLabels(rows);
  cache.set(rows, out);
  return out;
}

function computeConfigLabels(rows: ConfigRow[]): (string | null)[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const k = groupKey(r);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return rows.map((r) => {
    const k = groupKey(r);
    const total = counts.get(k) ?? 1;
    if (total < 2) return null;
    const n = (seen.get(k) ?? 0) + 1;
    seen.set(k, n);
    return `Config ${n} of ${total}`;
  });
}
